<i18n src="../translations"></i18n>

<template>
  <div v-if="items.length !== 0" class="pdp-info">
    <div v-if="title && title !== ''" class="headline">{{ title }}</div>
    <div class="wrapper" :class="{ 'filled-background': isFilledBackground }">
      <table :id="uid" class="table">
        <tbody>
          <tr
            v-for="item in limitedItems"
            :key="itemKey(item)"
            :class="[
              'row',
              item.modifier,
              {
                'block-separator': item.id && item.id.includes(separatorId),
              },
            ]"
          >
            <th class="cell label">{{ item.label }}</th>
            <td class="cell">
              <a
                v-if="item.attributeLink"
                class="value-link"
                :href="item.attributeLink"
                :data-testid="`attribute-link-${item.id}`"
              >
                {{ item.info }}
              </a>
              <span v-else>{{ item.info }}</span>
            </td>
          </tr>
        </tbody>

        <tbody v-if="hasOverflow && !isExpanded">
          <tr class="row">
            <td colspan="2">
              <button
                class="trigger"
                type="button"
                :aria-label="$t('product-info-sections.additional-attributes')"
                :aria-expanded="isExpanded ? 'true' : 'false'"
                :aria-controls="uid"
                @click="toggleMoreDetails()"
              >
                <strong class="trigger-text">
                  {{ $t('product-info-sections.additional-attributes') }}
                </strong>
                <ChevronRightSvg class="show-attributes" aria-hidden="true" />
              </button>
            </td>
          </tr>
        </tbody>
        <tbody v-else-if="hasOverflow">
          <tr class="row">
            <td colspan="2">
              <button
                class="trigger"
                type="button"
                :aria-label="$t('product-info-sections.less-attributes')"
                :aria-expanded="isExpanded ? 'true' : 'false'"
                :aria-controls="uid"
                @click="toggleMoreDetails()"
              >
                <strong class="trigger-text">
                  {{ $t('product-info-sections.less-attributes') }}
                </strong>
                <ChevronRightSvg class="hide-attributes" aria-hidden="true" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import ChevronRightSvg from 'Components/00-generated/ChevronRightSvg';
import { getLinesWithAttributeLinks } from 'Libs/helpers/attribute-catalog-search';

export default {
  name: 'PdpInfoAttributes',
  components: { ChevronRightSvg },
  props: {
    title: { type: String, default: '' },
    highlight: { type: Array, default: () => [] },
    regular: { type: Array, default: () => [] },
    defaultLimit: { type: Number, default: 7 },
    isFilledBackground: { type: Boolean, default: false },
  },
  data() {
    return {
      expanded: false,
      uid: `pdp-info-${Math.random().toString(36).slice(2, 9)}`,
      separatorId: 'block_separation_8',
    };
  },
  computed: {
    items() {
      const lines = [
        ...this.highlight.map((item) => ({
          ...item,
          modifier: 'highlight',
        })),
        ...this.regular.map((item) => ({
          ...item,
          modifier: 'regular',
        })),
      ];

      return getLinesWithAttributeLinks({
        lines,
        valueKey: 'info',
      });
    },
    limitedItems() {
      return this.items.slice(0, this.limitValue);
    },
    hasOverflow() {
      return this.items.length > this.defaultLimit;
    },
    isExpanded() {
      return this.expanded && this.items.length > 0;
    },
    limitValue() {
      const total = this.items.length;
      return this.expanded ? total : Math.min(total, this.defaultLimit);
    },
  },
  methods: {
    toggleMoreDetails() {
      this.expanded = !this.expanded;
    },
    itemKey(item) {
      return item.id || `${item.label}-${item.info}`;
    },
  },
};
</script>

<style scoped lang="scss">
@import 'variables/breakpoints';

.pdp-info {
  margin-bottom: var(--space-1--half);

  .headline {
    padding: 0 var(--space-1--half) var(--space-1--half) var(--space-XXS);
    width: 100%;
    font-weight: bold;
    font-size: var(--font-size-M);
    line-height: 17px;
    color: var(--color-nero);
    display: inline-block;
    border-bottom: 1px solid var(--color-silver-lining);
  }

  .wrapper {
    padding: 0 var(--space-2) 7px var(--space-2);
    border-bottom: 1px solid var(--color-silver-lining);

    &.filled-background {
      background: var(--color-alto-light);
    }

    .table {
      width: 100%;
      text-align: left;

      .row {
        border-bottom: 1px solid var(--color-alt);

        &.block-separator {
          border-bottom: 1px solid var(--color-rolling-stone);

          th,
          td {
            display: none;
          }
        }

        &:last-child {
          border-bottom: 0;
        }

        &.highlight {
          background: var(--color-wild-sand);

          .label {
            font-weight: bold;
          }

          & + & {
            .cell {
              padding-top: 0;
            }
          }
        }

        .cell {
          padding: 6px 0;
          font-size: var(--font-size-M);
          line-height: 1.2em;
          width: 50%;
          text-align: left;
          padding-right: 7px;
          color: var(--color-nero);
          word-break: break-all;

          + .cell {
            padding-right: 0;
            padding-left: 35px;
            word-break: break-word;
          }

          .value-link {
            color: inherit;
            text-decoration: underline;
          }
        }
      }

      .trigger {
        display: flex;
        align-items: center;
        cursor: pointer;
        margin: 6px 0 7px;
        padding: 0;

        .trigger-text {
          color: var(--color-nero);
          font-size: var(--font-size-SM);
          text-decoration: underline;
        }

        .show-attributes,
        .hide-attributes {
          color: var(--color-secondary);
          width: 22px;
          height: 18px;
          margin-top: var(--space-XXS);
        }

        .show-attributes {
          transform: rotate(90deg);
        }

        .hide-attributes {
          transform: rotate(270deg);
        }
      }
    }
  }
}

@media (min-width: $_widthSTo) {
  .pdp-info {
    .table {
      .row {
        &.highlight {
          & + & {
            .cell {
              padding-top: 0;
            }
          }
        }

        .cell {
          font-size: 16px;
          line-height: 18px;
        }
      }
    }
  }
}
</style>
